<template>
  <div v-if="!revision || !approvedFirmwareForm" class="flex items-center justify-center h-full">
    <ProgressSpinner/>
  </div>
  <div v-if="revision && approvedFirmwareForm"
       class="grid grid-cols-10 xl:grid-cols-9 gap-4 max-w-7xl mx-auto px-6 py-6">

    <Card class="order-2 md:order-1 col-span-10 xl:row-span-2 xl:col-span-3 md:col-span-3 xl:row-span-2">
      <template #content>
        <div class="flex items-center justify-center h-full -mt-4">
          <img class="w-full" :src="getImageUrl" alt=""/>
        </div>
      </template>
    </Card>

    <Card data-cy="revision-name" class="order-1 md:order-1 col-span-10 xl:col-span-6 md:col-span-7">
      <template #title>
        <h2 class="font-bold text-3xl mb-3">{{ revision.name }}</h2>
      </template>
      <template #subtitle>
        <p class="text-base">{{ revision.description }}</p>
      </template>
    </Card>

    <Card
        class="order-2 md:order-1 col-span-10 xl:col-span-3"
    >
      <template #title>
        {{ $t('pages.manufacturers.dashboard.manufacturers_firmware_update_dashboard_view.latest_available_firmware') }}
      </template>
      <template #subtitle>{{
          $t('pages.manufacturers.dashboard.manufacturers_firmware_update_dashboard_view.this_is_the_latest_available_firmware_for_this_device_revision')
        }}
      </template>
      <template #content>
        <FormGroup>
          <Dropdown
              filter
              disabled
              v-model="latestDeviceRevisionAvailableFirmwareId"
              :options="processedFirmwares"
              option-label="label"
              option-value="id"
              :placeholder="$t('pages.manufacturers.dashboard.manufacturers_firmware_update_dashboard_view.select_latest_firmware')"
          />
        </FormGroup>
      </template>
    </Card>

    <Card
        class="order-2 md:order-1 col-span-10 xl:col-span-3">
      <template #title>
        {{ $t('pages.manufacturers.dashboard.manufacturers_firmware_update_dashboard_view.current_target_firmware') }}
      </template>
      <template #subtitle>{{
          $t('pages.manufacturers.dashboard.manufacturers_firmware_update_dashboard_view.this_is_the_latest_firmware_approved_by_the_manufacturer_for_this_device_revision')
        }}
      </template>
      <template #content>
        <FormGroup :error-text="currentTargetFirmwareErrorMessage">
          <Dropdown
              filter
              @change="changeTargetFirmware"
              v-model="approvedFirmwareForm.default_firmware_id"
              :options="processedFirmwares"
              option-label="label"
              option-value="id"
              :placeholder="$t('pages.manufacturers.dashboard.manufacturers_firmware_update_dashboard_view.select_target_firmware')"
              show-clear
          />
        </FormGroup>
      </template>
    </Card>

    <DevicesByCurrentFirmware
        class="order-2 md:order-1 col-span-5 row-span-3 xl:col-span-3 col-span-3 xl:row-span-3"
        :data="chartData?.devices_by_current_firmware" :revision="revision"/>

    <DevicesByTargetFirmware
        class="order-2 md:order-1 col-span-5 row-span-3 xl:col-span-3 col-span-3 xl:row-span-3"
        :data="chartData?.devices_by_target_firmware" :revision="revision"/>

    <DeviceDashboardCard class="order-2 md:order-1 col-span-5 row-span-3 xl:col-span-3 xl:row-span-1"
                         :value="chartData && chartData.devices_running_manufacturers_target_firmware ? parseInt(chartData.devices_running_manufacturers_target_firmware / chartData.devices_count * 100) +'%' : 0"
                         :title="$t('common.devices')" :subtitle="$t('pages.manufacturers.dashboard.manufacturers_firmware_update_dashboard_view.running_manufacturers_target_firmware')"/>

    <DeviceDashboardCard class="order-2 md:order-1 col-span-5 row-span-3 xl:col-span-3 xl:row-span-2"
                         :value="chartData && chartData.devices_running_latest_available_firmware ? parseInt(chartData.devices_running_latest_available_firmware / chartData.devices_count * 100) +'%' : 0"
                         :title="$t('common.devices')" :subtitle="$t('pages.manufacturers.dashboard.manufacturers_firmware_update_dashboard_view.running_latest_firmware')"/>
  </div>
  <SaveApprovedFirmwareDialog :data="{previous: dialogOptions?.previous, new: dialogOptions?.new}"/>
</template>

<style scoped>

</style>

<script>
import ManufacturersSelectDeviceRevision from "@/Pages/Manufacturers/ManufacturersSelectDeviceRevision.vue";
import {mapStores} from "pinia";
import FormGroup from "@/Components/FormGroup.vue";
import {useDeviceRevisionsStore} from "@/stores/deviceRevisions.js";
import {useFirmwaresStore} from "@/stores/firmwares.js";
import {useApprovedFirmwaresStore} from "@/stores/approvedFirmwares.js";
import DevicesByCurrentFirmware from "@/Widgets/Charts/DevicesByCurrentFirmware.vue";
import DevicesByTargetFirmware from "@/Widgets/Charts/DevicesByTargetFirmware.vue";
import {useDashboardsStore} from "@/stores/dashboard.js";
import DeviceDashboardCard from "@/Widgets/Cards/DeviceDashboardCard.vue";
import SaveApprovedFirmwareDialog from "@/Pages/Manufacturers/Dialog/SaveApprovedFirmwareDialog.vue";
import {resolveRevisionImageUrl} from "@/Composables/DeviceImage.js";

export default {
  name: "ManufacturersFirmwareUpdateDashboardView",
  components: {
    SaveApprovedFirmwareDialog,
    ManufacturersSelectDeviceRevision,
    FormGroup,
    DevicesByCurrentFirmware,
    DevicesByTargetFirmware,
    DeviceDashboardCard
  },
  data() {
    return {
      revision: undefined,
      target_firmware_id: undefined,
      latest_available_firmware_id: undefined,
      chartData: undefined,
      approvedFirmwareId: undefined,
      approvedFirmwareForm: undefined,
      params: {
        include: 'deviceType,deviceModel',
      },
      approvedFirmwareParams: {
        filter: {
          device_revision_id: null,
          vehicle_manufacturer_id: null,
        }
      },
      dialogOptions : {
        new : undefined,
        previous : undefined,
      },
      chartDashboardParams: {
        filter: {
          id: null
        }
      },
      firmwareParams: {
        filter: {
          device_revision_ids: [],
        }
      },
    }
  },
  methods: {
    async refreshApprovedFirmware() {
      this.approvedFirmwareParams.filter.device_revision_id = this.$route.params.revision;
      this.approvedFirmwareParams.filter.vehicle_manufacturer_id = this.$route.params.manufacturer;

      try {
        await this.approvedFirmwaresStore.indexApprovedFirmwares(this.approvedFirmwareParams);

        let approvedFirmwares = this.approvedFirmwaresStore.getApprovedFirmwares;
        if (approvedFirmwares && approvedFirmwares.length === 0) {
          this.approvedFirmwareId = undefined;
          this.approvedFirmwareForm = await this.approvedFirmwaresStore.createApprovedFirmware();
          this.approvedFirmwareForm.device_revision_id = parseInt(this.$route.params.revision);
          this.approvedFirmwareForm.vehicle_manufacturer_id = parseInt(this.$route.params.manufacturer);
        } else {
          this.approvedFirmwareId = approvedFirmwares[0].id;
          this.approvedFirmwareForm = await this.approvedFirmwaresStore.editApprovedFirmware(approvedFirmwares[0].id);
        }
        this.dialogOptions.previous = this.approvedFirmwareForm.default_firmware_id;
      } catch (e) {
        console.log(e);
      }
    },
    async refreshFirmwares(deviceRevisionId) {
      this.firmwareParams.filter.device_revision_ids = [deviceRevisionId];
      return this.firmwaresStore.indexFirmwares(this.firmwareParams);
    },
    async refreshChartData() {
      this.chartDashboardParams.filter.device_revision_id = this.$route.params.revision;
      this.chartDashboardParams.filter.vehicle_manufacturer_id = this.$route.params.manufacturer;
      this.chartData = await this.dashboardsStore.getManufacturerDevices(this.chartDashboardParams);
    },
    changeTargetFirmware() {
      this.dialogOptions.new = this.approvedFirmwareForm.default_firmware_id
      this.$confirm.require({
        group: 'warning',
        message: this.$t('pages.manufacturers.dashboard.manufacturers_firmware_update_dashboard_view.are_you_sure_you_want_to_update_the_approved_firmware_for_this_device_revision'),
        header: this.$t('pages.manufacturers.dashboard.manufacturers_firmware_update_dashboard_view.save_approved_firmware'),
        // previous: this.firmwaresStore.getFirmwares.find(f => f.id === this.approvedFirmwareId) ?? [] ,
        // new: this.firmwaresStore.getFirmwares.find(f => f.id === this.approvedFirmwareForm.default_firmware_id) ?? [] ,
        accept: async () => {
          if (this.approvedFirmwareForm.default_firmware_id === null) {
            await this.approvedFirmwaresStore.deleteApprovedFirmware(this.approvedFirmwareId);
            await this.refreshChartData();
          } else {
            await this.saveApprovedFirmware();
          }
          await this.refreshApprovedFirmware();
        },
        reject: () => {
          this.$toast.add({
            severity: 'info',
            summary: this.$t('common.cancelled'),
            detail: this.$t('pages.manufacturers.dashboard.manufacturers_firmware_update_dashboard_view.approved_firmware_was_not_updated'),
            life: 3000
          });
          this.approvedFirmwareForm.reset('default_firmware_id');
        }
      });
    },
    async saveApprovedFirmware() {
      try {
        const response = await this.approvedFirmwareForm.submit();
        this.approvedFirmwareId = response.id;
        this.$toast.add({
          severity: 'success',
          summary: this.$t('common.success'),
          detail: this.$t('pages.manufacturers.dashboard.manufacturers_firmware_update_dashboard_view.updated_approved_firmware'),
          life: 3000
        });
        await this.refreshChartData();
      } catch (error) {
        this.$toast.add({
          severity: 'warn',
          summary: this.$t('common.error'),
          detail: this.$t('pages.manufacturers.dashboard.manufacturers_firmware_update_dashboard_view.approved_firmware_was_not_updated'),
          life: 3000
        });
        this.approvedFirmwareForm.reset('default_firmware_id');
      }
    },
  },
  computed: {
    ...mapStores(useDeviceRevisionsStore),
    ...mapStores(useFirmwaresStore),
    ...mapStores(useApprovedFirmwaresStore),
    ...mapStores(useDashboardsStore),
    latestDeviceRevisionAvailableFirmwareId() {
      let firmwares = this.firmwaresStore.getFirmwares;
      if (firmwares) {
        let lastFirmware = firmwares.slice(-1).pop();
        return lastFirmware ? lastFirmware.id : undefined;
      }
      return undefined;
    },
    processedFirmwares() {
      return this.firmwaresStore.getFirmwares?.map(firmware => ({
        ...firmware,
        label: this.$t('pages.manufacturers.dashboard.manufacturers_firmware_update_dashboard_view.firmware_x_version_x', { firmware: firmware.name ?? this.$t('common.na'), version: firmware.version_number })
      })) || [];
    },
    currentTargetFirmwareErrorMessage() {
      if (this.latestDeviceRevisionAvailableFirmwareId !== this.approvedFirmwareForm.default_firmware_id) {
        return this.$t('pages.manufacturers.dashboard.manufacturers_firmware_update_dashboard_view.currently_not_tracking_the_latest_version');
      }
    },
    getImageUrl() {
      return resolveRevisionImageUrl(this.revision, 'https://placehold.co/600x400');
    },
  },
  watch: {
    '$route.params': {
      async handler(newValue, oldValue) {

        if (newValue.revision) {
          try {
            this.revision = await this.deviceRevisionsStore.showDeviceRevision(newValue.revision, this.params);
            await this.refreshFirmwares(this.revision.id);
          } catch (e) {
            this.revision = undefined;
          }
        } else {
          this.revision = undefined;
        }

        this.refreshChartData();
        this.refreshApprovedFirmware();
      },
      immediate: true,
    }
  },
}
</script>

<style scoped>

</style>
